import { isNativeApp } from './platform';

/**
 * Which build is running, and where.
 *
 * Every conclusion in the iOS voice-chat investigation stayed conditional
 * because neither of these existed: the .ipa carries a frozen bundle with no
 * version in it (package.json has said "1.0.0" forever), and `isNativeApp()`
 * drove ten UI decisions without ever reaching telemetry, so native sessions
 * had to be inferred from CORS preflights in the proxy log.
 *
 * Injected by vite `define` at build time — see vite.config.mjs. The fallbacks
 * keep this importable from tests and any tooling that does not go through
 * vite.
 */

export const BUILD_SHA: string =
  typeof __BUILD_SHA__ === 'string' ? __BUILD_SHA__ : 'unknown';

/** The vite mode the bundle was built with: 'ios', 'ios-staging', 'production'… */
export const BUILD_MODE: string =
  typeof __BUILD_MODE__ === 'string' ? __BUILD_MODE__ : 'unknown';

/**
 * The App Store build number (CURRENT_PROJECT_VERSION), or 0 on web and in any
 * context that did not go through vite.
 *
 * Distinct from BUILD_SHA because this one has to *order*: the server's version
 * gate asks whether this build is below a floor, which a sha cannot answer. 0
 * is never gated, so the fallback is also the safe value.
 */
export const BUILD_NUMBER: number =
  typeof __APP_BUILD__ === 'number' ? __APP_BUILD__ : 0;

/**
 * Where the code is running, as opposed to what it was built for. A staging
 * bundle side-loaded onto a device still reports 'ios'; BUILD_MODE is what
 * says which backend it was aimed at.
 */
export const platform = (): 'ios' | 'web' => (isNativeApp() ? 'ios' : 'web');

/**
 * The identity markers, in the shape both the /connect body and the analytics
 * super-properties want. Snake_case because that is what the server columns
 * and the Mixpanel properties are called.
 */
export const buildMarkers = (): {
  build_sha: string;
  build_mode: string;
  platform: 'ios' | 'web';
} => ({
  build_sha: BUILD_SHA,
  build_mode: BUILD_MODE,
  platform: platform(),
});

/** The request header carrying client identity to the version gate. */
export const CLIENT_HEADER = 'X-Aleph-Client';

/**
 * Client identity for the endpoints that carry a breaking contract, as
 * `platform/build` — 'ios/13', 'web/0'.
 *
 * Deliberately one header on the call sites that need it rather than a global
 * `fetch` patch: a side effect on every request would confuse the next person
 * debugging an auth callback on iOS, and the only call that has to be
 * understood by the server is the reader payload. Other call sites can adopt
 * it for telemetry breadth; none is required for the gate to work.
 *
 * No CORS change is needed — the API's CORSMiddleware sends
 * `allow_headers=["*"]`, so this passes preflight as-is.
 */
export const clientIdentityHeaders = (): Record<string, string> => ({
  [CLIENT_HEADER]: `${platform()}/${BUILD_NUMBER}`,
});

/**
 * Whether developer-only surfaces should render — currently the offline
 * storage card on the profile page.
 *
 * True under the vite dev server and in the staging native build, which is
 * what goes on a test device. Never true for the production native bundle or
 * the deployed web app, so nothing here needs to be presentable.
 */
export const isDevSurfaceEnabled = (): boolean =>
  import.meta.env.DEV || BUILD_MODE === 'ios-staging';
